import { memo, useRef, useState, useEffect, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";

import { prefersReducedMotion } from "@/animations";

import styles from "./styles.module.css";

const SEGMENTS = 56;
const SIZE = 3.6;
/** 与曲面 mesh 一致的倾斜，使轴对齐 z = f(x, y) */
const SURFACE_ROTATION: [number, number, number] = [-Math.PI / 2.15, 0, 0.15];

/** z = f(x, y, t) — 动态波面 + 次级扰动，模拟空间水质场 */
function sampleHeight(x: number, y: number, t: number): number {
  const r = Math.sqrt(x * x + y * y);
  return (
    Math.sin(r * 2.4 - t * 1.35) * 0.42 * Math.exp(-r * 0.22) +
    Math.sin(x * 1.7 + t * 0.7) * 0.14 +
    Math.cos(y * 1.9 - t * 0.55) * 0.11
  );
}

function AxisArm({
  tx,
  ty,
  tz,
  color,
  label,
  lx,
  ly,
  lz,
}: {
  tx: number;
  ty: number;
  tz: number;
  color: string;
  label: string;
  lx: number;
  ly: number;
  lz: number;
}) {
  const { mid, quat, length } = useMemo(() => {
    const end = new THREE.Vector3(tx, ty, tz);
    const length = end.length();
    const mid = end.clone().multiplyScalar(0.5);
    const quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      end.clone().normalize(),
    );
    return { mid, quat, length };
  }, [tx, ty, tz]);

  return (
    <group>
      <mesh position={mid} quaternion={quat}>
        <cylinderGeometry args={[0.028, 0.028, length, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh position={[tx, ty, tz]}>
        <sphereGeometry args={[0.065, 12, 12]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <Text
        position={[lx, ly, lz]}
        fontSize={0.3}
        color={color}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.025}
        outlineColor="#041828"
      >
        {label}
      </Text>
    </group>
  );
}

/** 曲面局部坐标：X 红、Y 绿、Z 蓝（高度） */
function AxisHelper() {
  const len = 1.9;
  const zLen = len * 0.8;
  return (
    <group rotation={SURFACE_ROTATION}>
      <AxisArm tx={len} ty={0} tz={0} color="#ff5a5a" label="X" lx={len + 0.28} ly={0} lz={0.1} />
      <AxisArm tx={0} ty={len} tz={0} color="#4ade80" label="Y" lx={0.1} ly={len + 0.28} lz={0.1} />
      <AxisArm tx={0} ty={0} tz={zLen} color="#5b9dff" label="Z" lx={0.1} ly={0.1} lz={zLen + 0.28} />
      <mesh>
        <sphereGeometry args={[0.05, 12, 12]} />
        <meshBasicMaterial color="#e8f4ff" />
      </mesh>
    </group>
  );
}

function FunctionSurface() {
  const meshRef = useRef<THREE.Mesh>(null);
  const wireRef = useRef<THREE.Mesh>(null);
  const colors = useMemo(() => new Float32Array((SEGMENTS + 1) * (SEGMENTS + 1) * 3), []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const update = (mesh: THREE.Mesh | null, paint: boolean) => {
      if (!mesh) return;
      const geo = mesh.geometry as THREE.PlaneGeometry;
      const pos = geo.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = sampleHeight(x, y, t);
        pos.setZ(i, z);
        if (paint) {
          const n = (z + 0.55) / 1.1;
          colors[i * 3] = 0.15 + n * 0.25;
          colors[i * 3 + 1] = 0.55 + n * 0.35;
          colors[i * 3 + 2] = 0.65 + n * 0.3;
        }
      }
      pos.needsUpdate = true;
      if (paint) {
        let colorAttr = geo.getAttribute("color") as THREE.BufferAttribute | null;
        if (!colorAttr) {
          colorAttr = new THREE.BufferAttribute(colors, 3);
          geo.setAttribute("color", colorAttr);
        } else {
          (colorAttr.array as Float32Array).set(colors);
          colorAttr.needsUpdate = true;
        }
      }
      geo.computeVertexNormals();
    };

    update(meshRef.current, true);
    update(wireRef.current, false);
  });

  return (
    <group position={[0.35, 0.05, 0]}>
      <AxisHelper />
      <mesh ref={meshRef} rotation={SURFACE_ROTATION}>
        <planeGeometry args={[SIZE, SIZE, SEGMENTS, SEGMENTS]} />
        <meshStandardMaterial
          vertexColors
          side={THREE.DoubleSide}
          metalness={0.25}
          roughness={0.4}
          transparent
          opacity={0.88}
        />
      </mesh>
      <mesh ref={wireRef} rotation={SURFACE_ROTATION} position={[0, 0.01, 0]}>
        <planeGeometry args={[SIZE, SIZE, Math.floor(SEGMENTS / 2), Math.floor(SEGMENTS / 2)]} />
        <meshBasicMaterial color="#9fe8de" wireframe transparent opacity={0.28} />
      </mesh>
      <SamplePoints />
    </group>
  );
}

function SamplePoints() {
  const ref = useRef<THREE.Points>(null);
  const base = useMemo(() => {
    const n = 36;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const r = 0.4 + (i % 5) * 0.28;
      arr[i * 3] = Math.cos(a) * r;
      arr[i * 3 + 1] = 0;
      arr[i * 3 + 2] = Math.sin(a) * r;
    }
    return arr;
  }, []);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    const pos = ref.current.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3];
      const z = base[i * 3 + 2];
      pos.setXYZ(i, x, sampleHeight(x, z, t) + 0.12, z);
    }
    pos.needsUpdate = true;
  });

  return (
    <points ref={ref} rotation={SURFACE_ROTATION}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[base.slice(), 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.07} color="#f0c35a" sizeAttenuation depthWrite={false} />
    </points>
  );
}

function GridFloor() {
  return (
    <gridHelper
      args={[5.5, 12, "#1f5a6a", "#0d3344"]}
      position={[0.35, -0.95, 0]}
      rotation={[0, 0.15, 0]}
    />
  );
}

function Scene() {
  return (
    <>
      <color attach="background" args={["#06243a"]} />
      <ambientLight intensity={0.55} />
      <directionalLight position={[5, 7, 4]} intensity={1.15} color="#dff6ff" />
      <pointLight position={[-2, 3, 2]} intensity={0.7} color="#6fd3c2" />
      <FunctionSurface />
      <GridFloor />
      <fog attach="fog" args={["#06243a", 7, 14]} />
      <OrbitControls
        makeDefault
        enablePan={false}
        enableDamping
        dampingFactor={0.08}
        minDistance={2.4}
        maxDistance={9}
        minPolarAngle={0.3}
        maxPolarAngle={Math.PI / 2.05}
        autoRotate
        autoRotateSpeed={0.55}
        target={[0.35, 0.05, 0]}
      />
    </>
  );
}

function HeroCanvas() {
  return (
    <Canvas
      className={styles.canvas}
      camera={{ position: [3.4, 2.4, 4.2], fov: 40 }}
      dpr={[1, 1.75]}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
    >
      <Scene />
    </Canvas>
  );
}

export const WaterHeroScene = memo(function WaterHeroScene() {
  const [ready, setReady] = useState(false);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    setReady(true);
  }, []);

  if (reduced || !ready) {
    return <div className={styles.fallback} aria-hidden="true" />;
  }

  return (
    <div className={styles.wrap} aria-label="Interactive 3D water quality field">
      <HeroCanvas />
    </div>
  );
});

export default WaterHeroScene;
